import * as React from "react";
import { Link } from "react-router-dom";
import Accordion from "@mui/material/Accordion";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";

import { roundToThree } from "../../common/utils";
import { COMPOUND_TYPE } from "../../common/constants";
import { Paths, DATA_PLATFORM_TYPE } from "../../common/constants";
import { Cannabinoid, ProductChainDetails, Terpene } from "./services/types";

interface IKeyComponentProps {
  productChainDetails: ProductChainDetails | null;
}

export default function KeyComponent({
  productChainDetails,
}: IKeyComponentProps) {
  if (!productChainDetails) return null;

  const isValidValue = (value: string | undefined | null) => {
    return Boolean(value) && value !== "";
  };

  const chooseNordicDisplay = (weightValue?: string) => {
    const trimmed = weightValue?.trim();

    // If value contains < or > prefix, return as it is
    if (trimmed?.startsWith("<") || trimmed?.startsWith(">")) {
      return trimmed;
    }

    // If value is ND, NR, N/A → return as is
    if (trimmed && ["ND", "NR", "N/A"].includes(trimmed)) {
      return trimmed;
    }

    if (isValidValue(weightValue) && weightValue) {
      const cleaned = weightValue?.replace(/%$/g, "");
      if (cleaned === "0") return cleaned;
      else return `${cleaned}%`;
    }

    return "Not Reported";
  };

  const formatNordicCompoundValue = (value?: string) => {
    if (!isValidValue(value)) {
      return "Not Reported";
    } else if (value) {
      const trimmed = value?.trim();

      // If value contains < or > prefix, return as it is
      if (trimmed?.startsWith("<") || trimmed?.startsWith(">")) {
        return trimmed;
      }

      // If value is ND, NR, N/A → return as is
      if (trimmed && ["ND", "NR", "N/A"].includes(trimmed)) {
        return trimmed;
      }

      // Remove % sign before rounding
      const cleaned = trimmed.replace(/%$/g, "");
      const rounded = roundToThree(cleaned);

      if (Number(rounded) === 0) return "0";
      return `${rounded}%`;
    }
  };
  return (
    <div className="infoAccordionWrapper pt-0">
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <h4 className="accordionTitle">Key Components</h4>
        </AccordionSummary>
        <AccordionDetails>
          <>
            {/* === Cannabinoid Section === */}
            <ul className="listWrapper strainTypeWrapper">
              <li className="list">
                <span className="key fontGrey">Strain Type: </span>
                <span className="value">
                  {productChainDetails?.strainType || "-"}
                </span>
              </li>
            </ul>
            <div className="productDetailsTableWrapper keycomponentstable">
              <div className="topHead">
                <h2 className="title">Cannabinoid</h2>
                {productChainDetails?.keyComponents?.topThreeCannabinoids
                  ?.length > 0 && (
                  <Link
                    to={`${Paths.COMPOUND_LISTING}/${COMPOUND_TYPE.CANNABINOID}/${productChainDetails?.climsId}`}
                    className="link"
                  >
                    View All
                  </Link>
                )}
              </div>

              <ul className="listWrapper">
                <>
                  {productChainDetails?.keyComponents?.topThreeCannabinoids?.map(
                    (cannabinoid: Cannabinoid, index) => (
                      <li key={index} className="list">
                        <Link
                          className="green-color key"
                          to={
                            cannabinoid.contentId
                              ? `${Paths.COMPOUND_DETAILS}/${cannabinoid?.contentId}`
                              : `${Paths.COMPOUND_DETAILS}`
                          }
                          onClick={() =>
                            localStorage.setItem(
                              "compoundName",
                              cannabinoid?.displayName,
                            )
                          }
                        >
                          {cannabinoid?.displayName || "-"}
                        </Link>

                        <span className="value">
                          {productChainDetails?.dataPlatform ===
                          DATA_PLATFORM_TYPE.NORDIC_ANALYTICAL
                            ? formatNordicCompoundValue(cannabinoid?.value)
                            : cannabinoid?.value
                              ? `${roundToThree(cannabinoid.value)}%`
                              : "Not Reported"}
                        </span>
                      </li>
                    ),
                  )}

                  <li className="list boldList">
                    <span className="key black_key">Total THC:</span>
                    <span className="value">
                      {productChainDetails?.dataPlatform ===
                      DATA_PLATFORM_TYPE.NORDIC_ANALYTICAL
                        ? chooseNordicDisplay(productChainDetails?.maxThcWeight)
                        : productChainDetails?.keyComponents?.thcTotal
                          ? `${roundToThree(productChainDetails?.keyComponents?.thcTotal)}%`
                          : "Not Reported"}
                    </span>
                  </li>

                  <li className="list boldList">
                    <span className="key black_key">Total CBD:</span>
                    <span className="value">
                      {productChainDetails?.dataPlatform ===
                      DATA_PLATFORM_TYPE.NORDIC_ANALYTICAL
                        ? chooseNordicDisplay(productChainDetails?.maxCbdWeight)
                        : productChainDetails?.keyComponents?.cbdTotal
                          ? `${roundToThree(productChainDetails?.keyComponents?.cbdTotal)}%`
                          : "Not Reported"}
                    </span>
                  </li>

                  <li className="list boldList">
                    <span className="key bold_key">Total Cannabinoid:</span>
                    <span className="value bold_key">
                      {productChainDetails?.dataPlatform ===
                      DATA_PLATFORM_TYPE.NORDIC_ANALYTICAL
                        ? chooseNordicDisplay(
                            productChainDetails?.totalCannabinoidsWeight,
                          )
                        : productChainDetails?.keyComponents?.cannabinoidTotal
                          ? `${roundToThree(productChainDetails?.keyComponents?.cannabinoidTotal)}%`
                          : "Not Reported"}
                    </span>
                  </li>
                </>
              </ul>
            </div>

            {/* === Terpene Section === */}
            <div className="productDetailsTableWrapper keycomponentstable">
              <div className="topHead">
                <h2 className="title">Terpene</h2>

                {productChainDetails?.keyComponents?.topThreeTerpenes?.length >
                  0 && (
                  <Link
                    to={`${Paths.COMPOUND_LISTING}/${COMPOUND_TYPE.TERPENE}/${productChainDetails?.climsId}`}
                    className="link"
                  >
                    View All
                  </Link>
                )}
              </div>

              <ul className="listWrapper">
                <>
                  {productChainDetails?.keyComponents?.topThreeTerpenes?.map(
                    (terpene: Terpene, index) => (
                      <li key={index} className="list">
                        <Link
                          className="green-color key"
                          to={
                            terpene.contentId
                              ? `${Paths.COMPOUND_DETAILS}/${terpene?.contentId}`
                              : `${Paths.COMPOUND_DETAILS}`
                          }
                          onClick={() =>
                            localStorage.setItem(
                              "compoundName",
                              terpene?.displayName,
                            )
                          }
                        >
                          {terpene?.displayName || "-"}
                        </Link>

                        <span className="value">
                          {productChainDetails?.dataPlatform ===
                          DATA_PLATFORM_TYPE.NORDIC_ANALYTICAL
                            ? formatNordicCompoundValue(terpene?.value)
                            : terpene?.value
                              ? `${roundToThree(terpene.value)}%`
                              : "Not Reported"}
                        </span>
                      </li>
                    ),
                  )}

                  <li className="list boldList">
                    <span className="key bold_key">Total Terpenes:</span>
                    <span className="value bold_key">
                      {productChainDetails?.dataPlatform ===
                      DATA_PLATFORM_TYPE.NORDIC_ANALYTICAL
                        ? chooseNordicDisplay(
                            productChainDetails?.totalTerpenesPercentWeight,
                          )
                        : productChainDetails?.keyComponents?.terpeneTotal
                          ? `${roundToThree(productChainDetails?.keyComponents?.terpeneTotal)}%`
                          : "Not Reported"}
                    </span>
                  </li>
                </>
              </ul>
            </div>
          </>
        </AccordionDetails>
      </Accordion>
    </div>
  );
}
